import React, { useEffect, useState } from 'react';
import { CalendarRange, CalendarX, Lock, Trash2, X } from 'lucide-react';

import SelectorDeFechas, { formatearFechaCorta } from './SelectorDeFechas';
import './BloquearHorarios.css';

/**
 * Ventana para bloquear horarios.
 *
 * Antes era un asistente de cuatro pasos que aplicaba un solo horario a todas
 * las fechas elegidas. El caso real más común no entra en esa forma: al pasar en
 * limpio los turnos del día se bloquean franjas sueltas, cada una con su hora y
 * su servicio, y con el asistente había que recorrer el formulario completo y
 * reabrir la ventana una vez por franja —unos treinta clics para tres bloqueos—.
 *
 * Acá cada día que se toca en el calendario aparece como una fila propia a la
 * derecha, con su horario, su destino y su motivo. Volver a tocarlo lo saca. Un
 * rango sí es una sola fila, porque el caso del rango es "cerrado por
 * vacaciones", no un horario distinto por día. Al confirmar sale un solo POST
 * con todas las filas.
 *
 * Cada fila dice además a QUIÉN le aplica, que es lo que vuelve a esta ventana
 * una sola herramienta para tres cosas que antes no tenían dónde ir: sin destino
 * cierra el negocio entero (feriado, vacaciones, cerrar más temprano), con una
 * persona marca que esa persona no está (franco, licencia, se va antes). El
 * destino se guarda en el bloqueo y el backend lo respeta al aprobar reservas:
 * que falte alguien no cierra el horario si queda otra persona que puede cubrir
 * ese servicio.
 *
 * Los turnos que entran por fuera de la app NO van acá: son turnos de verdad, con
 * cliente y con una duración que sale del servicio, y viven en AgregarTurno.jsx.
 *
 * Los cuatro pasos además arrastraban tres bugs que este componente no repite:
 * el paso 3 hacía `setCurrentStep(4)` al elegir "horarios específicos", así que
 * los inputs de hora eran inalcanzables; `selectedDates` arrancaba en `['']` y
 * el resumen mostraba un "Invalid Date" que nadie había elegido; y el POST
 * mandaba un array a un endpoint que esperaba un objeto, o sea que bloquear
 * fallaba con 400 siempre.
 *
 * El overlay es propio y no el Dialog de Radix, para poder controlar el ancho y
 * las dos columnas sin pelearle a las clases de `ui/dialog.jsx` (`max-w-lg`,
 * `grid`, `p-6`). Como se renderiza adentro de `.dashboard-container`, que apaga
 * todo `transform` y toda `animation` con `!important`, el centrado va por flex
 * y no por `translate`. Lo que Radix daba gratis —Escape, clic afuera y bloqueo
 * del scroll de atrás— está escrito abajo.
 */

const contarDias = (fila) => {
  if (fila.tipo !== 'rango') return 1;
  const unDia = 24 * 60 * 60 * 1000;
  const desde = new Date(`${fila.desdeFecha}T00:00:00`);
  const hasta = new Date(`${fila.hastaFecha}T00:00:00`);
  return Math.round((hasta - desde) / unDia) + 1;
};

const filaIncompleta = (fila) =>
  !fila.todoElDia && (!fila.desde || !fila.hasta || fila.hasta <= fila.desde);

export default function BloquearHorarios({
  abierto,
  onCerrar,
  onConfirmar,
  personal = [],
  guardando,
  error,
}) {
  const [modo, setModo] = useState('sueltos');
  const [rango, setRango] = useState({ desde: '', hasta: '' });
  const [bloqueos, setBloqueos] = useState([]);

  // Hoja en blanco cada vez que se abre. El componente no se desmonta al cerrar
  // —sale por `return null`—, así que sin esto quedarían las filas anteriores.
  useEffect(() => {
    if (!abierto) return;
    setModo('sueltos');
    setRango({ desde: '', hasta: '' });
    setBloqueos([]);
  }, [abierto]);

  // Escape y clic afuera no cierran si hay filas cargadas: son los dos caminos
  // que se disparan sin querer, y perder ocho franjas por un Escape de más sería
  // peor que el clic extra en Cancelar.
  useEffect(() => {
    if (!abierto) return undefined;

    const alApretar = (e) => {
      if (e.key === 'Escape' && bloqueos.length === 0) onCerrar();
    };
    document.addEventListener('keydown', alApretar);

    const scrollPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', alApretar);
      document.body.style.overflow = scrollPrevio;
    };
  }, [abierto, onCerrar, bloqueos.length]);

  if (!abierto) return null;


  const fechasElegidas = bloqueos.filter((b) => b.tipo === 'dia').map((b) => b.fecha);

  // La fecha alcanza como identidad: un día suelto no puede estar dos veces —el
  // segundo clic lo saca— y un rango se distingue por sus dos extremos.
  const claveDeFila = (fila) =>
    fila.tipo === 'rango' ? `r:${fila.desdeFecha}:${fila.hastaFecha}` : `d:${fila.fecha}`;

  // Los días sueltos primero por fecha, y los rangos por su día de inicio, así
  // la lista sigue el mismo orden sin importar en qué orden se fue armando.
  const ordenar = (filas) =>
    [...filas].sort((a, b) =>
      (a.fecha || a.desdeFecha).localeCompare(b.fecha || b.desdeFecha)
    );

  const filaNueva = (extra) => ({
    todoElDia: true,
    desde: '',
    hasta: '',
    staffId: '',
    motivo: '',
    ...extra,
  });

  // En días sueltos el calendario y la lista son la misma cosa: tocar un día lo
  // suma como fila, volver a tocarlo la saca. Sin botón "agregar" en el medio.
  const alElegirFechas = (nuevas) => {
    setBloqueos((previos) => {
      const sobreviven = previos.filter(
        (b) => b.tipo !== 'dia' || nuevas.includes(b.fecha)
      );
      const yaEstan = sobreviven.filter((b) => b.tipo === 'dia').map((b) => b.fecha);
      const agregadas = nuevas
        .filter((f) => !yaEstan.includes(f))
        .map((fecha) => filaNueva({ tipo: 'dia', fecha }));
      return ordenar([...sobreviven, ...agregadas]);
    });
  };

  // Un rango se agrega entero al cerrarlo, y el selector queda libre para el
  // siguiente.
  const alElegirRango = (nuevo) => {
    if (!nuevo.desde || !nuevo.hasta) {
      setRango(nuevo);
      return;
    }
    const fila = filaNueva({
      tipo: 'rango',
      desdeFecha: nuevo.desde,
      hastaFecha: nuevo.hasta,
    });
    setBloqueos((previos) =>
      // Marcar dos veces el mismo período no agrega un segundo bloqueo idéntico.
      previos.some((b) => claveDeFila(b) === claveDeFila(fila))
        ? previos
        : ordenar([...previos, fila])
    );
    setRango({ desde: '', hasta: '' });
  };

  const actualizar = (clave, cambios) =>
    setBloqueos((previos) =>
      previos.map((fila) =>
        claveDeFila(fila) === clave ? { ...fila, ...cambios } : fila
      )
    );

  const quitar = (clave) =>
    setBloqueos((previos) => previos.filter((b) => claveDeFila(b) !== clave));

  const totalDias = bloqueos.reduce((suma, fila) => suma + contarDias(fila), 0);
  const incompletas = bloqueos.filter(filaIncompleta).length;

  const confirmar = () => {
    const payload = bloqueos.map((fila) => {
      const persona = personal.find((p) => p.id === fila.staffId);
      const comun = {
        isFullDay: fila.todoElDia,
        startTime: fila.todoElDia ? null : fila.desde,
        endTime: fila.todoElDia ? null : fila.hasta,
        reason: fila.motivo.trim(),
        // Sin persona el bloqueo cierra el negocio entero. Van el id y el nombre
        // porque el backend empareja por uno o por otro según de dónde venga el
        // turno que está validando.
        staffId: fila.staffId || null,
        staffName: persona?.name || null,
      };

      return fila.tipo === 'rango'
        ? {
            ...comun,
            type: 'range',
            rangeStartDate: fila.desdeFecha,
            rangeEndDate: fila.hastaFecha,
          }
        : { ...comun, type: 'single', date: fila.fecha };
    });

    onConfirmar(payload, () => setBloqueos([]));
  };

  return (
    <div
      className="bloquear-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && bloqueos.length === 0) onCerrar();
      }}
    >
      <div
        className="bloquear-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bloquear-titulo"
      >
        <header className="bloquear-panel__cabecera">
          <h3 id="bloquear-titulo">
            <Lock size={18} aria-hidden="true" />
            Bloquear horarios
          </h3>
          <button type="button" onClick={onCerrar} aria-label="Cerrar">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="bloquear-panel__cuerpo">
          {/* ===== Izquierda: elegir los días ===== */}
          <section className="bloquear-calendario">
            <div className="bloquear-modo" role="group" aria-label="Tipo de bloqueo">
              <button
                type="button"
                onClick={() => {
                  setModo('sueltos');
                  setRango({ desde: '', hasta: '' });
                }}
                aria-pressed={modo === 'sueltos'}
              >
                <CalendarX size={17} aria-hidden="true" />
                Días sueltos
              </button>
              <button
                type="button"
                onClick={() => setModo('rango')}
                aria-pressed={modo === 'rango'}
              >
                <CalendarRange size={17} aria-hidden="true" />
                Rango
              </button>
            </div>

            <SelectorDeFechas
              modo={modo}
              fechas={fechasElegidas}
              rango={rango}
              onCambiarFechas={alElegirFechas}
              onCambiarRango={alElegirRango}
            />

            <p className="bloquear-calendario__ayuda">
              {modo === 'sueltos'
                ? 'Cada día que toques se suma a la derecha con su propio horario. Tocalo de nuevo para sacarlo.'
                : 'Marcá el primer y el último día. El período entra como un solo bloqueo.'}
            </p>
          </section>

          {/* ===== Derecha: una fila por bloqueo ===== */}
          <section className="bloquear-filas">
            <h4>
              A bloquear
              {bloqueos.length > 0 && (
                <span className="bloquear-filas__cuenta">{bloqueos.length}</span>
              )}
            </h4>

            {bloqueos.length === 0 ? (
              <p className="bloquear-filas__vacia">
                Elegí los días en el calendario. Cada uno aparece acá y podés darle su
                horario y decir si cierra todo el negocio o sólo falta una persona.
                Podés juntar varios distintos y bloquearlos de una sola vez.
              </p>
            ) : (
              <ul>
                {bloqueos.map((fila) => {
                  const incompleta = filaIncompleta(fila);
                  const clave = claveDeFila(fila);

                  return (
                    <li key={clave} className={incompleta ? 'es-incompleta' : ''}>
                      <div className="bloquear-fila__titulo">
                        <span className="bloquear-fila__fecha">
                          {fila.tipo === 'rango'
                            ? `${formatearFechaCorta(fila.desdeFecha)} → ${formatearFechaCorta(
                                fila.hastaFecha
                              )}`
                            : formatearFechaCorta(fila.fecha)}
                          {fila.tipo === 'rango' && (
                            <small>{contarDias(fila)} días</small>
                          )}
                        </span>

                        <div
                          className="bloquear-fila__cuando"
                          role="group"
                          aria-label={`Horario del ${
                            fila.tipo === 'rango' ? 'período' : 'día'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => actualizar(clave, { todoElDia: true })}
                            aria-pressed={fila.todoElDia}
                          >
                            Todo el día
                          </button>
                          <button
                            type="button"
                            onClick={() => actualizar(clave, { todoElDia: false })}
                            aria-pressed={!fila.todoElDia}
                          >
                            Sólo estas horas
                          </button>
                        </div>

                        <button
                          type="button"
                          className="bloquear-fila__quitar"
                          onClick={() => quitar(clave)}
                          aria-label="Quitar este bloqueo"
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </div>

                      <div className="bloquear-fila__campos">
                        {!fila.todoElDia && (
                          <div className="bloquear-fila__horas">
                            <label>
                              <span>Desde</span>
                              <input
                                type="time"
                                value={fila.desde}
                                onChange={(e) =>
                                  actualizar(clave, { desde: e.target.value })
                                }
                              />
                            </label>
                            <label>
                              <span>Hasta</span>
                              <input
                                type="time"
                                value={fila.hasta}
                                onChange={(e) =>
                                  actualizar(clave, { hasta: e.target.value })
                                }
                              />
                            </label>
                          </div>
                        )}

                        {personal.length > 0 && (
                          <label className="bloquear-fila__servicio">
                            <span>A quién</span>
                            <select
                              value={fila.staffId}
                              onChange={(e) => actualizar(clave, { staffId: e.target.value })}
                            >
                              <option value="">Todo el negocio</option>
                              {personal.map((persona) => (
                                <option key={persona.id} value={persona.id}>
                                  Sólo {persona.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}

                        <label className="bloquear-fila__motivo">
                          <span>Motivo</span>
                          <input
                            type="text"
                            value={fila.motivo}
                            onChange={(e) => actualizar(clave, { motivo: e.target.value })}
                            placeholder="Opcional"
                          />
                        </label>
                      </div>

                      {incompleta && (
                        <p className="bloquear-fila__falta">
                          {!fila.desde || !fila.hasta
                            ? 'Falta la hora de inicio o de fin.'
                            : 'La hora de fin tiene que ser posterior a la de inicio.'}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <footer className="bloquear-panel__pie">
          {error && <p className="bloquear-error">{error}</p>}

          <span className="bloquear-panel__total">
            {totalDias > 0 && `${totalDias} ${totalDias === 1 ? 'día' : 'días'} en total`}
            {incompletas > 0 &&
              ` · ${incompletas} sin completar`}
          </span>

          <button type="button" className="bloquear-cancelar" onClick={onCerrar}>
            Cancelar
          </button>

          <button
            type="button"
            className="bloquear-confirmar"
            onClick={confirmar}
            disabled={bloqueos.length === 0 || incompletas > 0 || guardando}
          >
            <Lock size={17} aria-hidden="true" />
            {guardando
              ? 'Bloqueando…'
              : bloqueos.length > 1
              ? `Bloquear los ${bloqueos.length}`
              : 'Bloquear'}
          </button>
        </footer>
      </div>
    </div>
  );
}
